/**
 * CoverLetterDialog — modal that generates an AI cover letter for a job and
 * lets the teacher edit, copy, or save it. Charges TeeBee Coins.
 */
import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Copy, X, Coins } from "lucide-react";
import { toast } from "sonner";

const TONES = [
  { v: "warm",     label: "Warm" },
  { v: "formal",   label: "Formal" },
  { v: "concise",  label: "Concise" },
  { v: "creative", label: "Creative" },
];
const VERSIONS = [
  { v: "full",     label: "Full letter",   hint: "Cover-letter PDF style" },
  { v: "email",    label: "Email",          hint: "110-150 word email" },
  { v: "whatsapp", label: "WhatsApp",       hint: "50-80 word polite ping" },
];

export default function CoverLetterDialog({ open, onClose, job }) {
  const [tone, setTone]       = useState("warm");
  const [version, setVersion] = useState("full");
  const [note, setNote]       = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open || !job) return null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/marketplace/cover-letter", {
        job_id: job.job_id, tone, version, custom_note: note, save: true,
      });
      setContent(data.content);
      toast.success(`Cover letter ready · ${data.coins_spent} coins`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch { toast.error("Could not copy"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-3" onClick={onClose} data-testid="cover-letter-dialog">
      <div className="bg-card rounded-3xl border border-border max-w-2xl w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Sparkles className="h-3 w-3" /> AI Cover letter
            </span>
            <p className="font-display text-lg font-medium tracking-tight mt-2">{job.role_title}</p>
            <p className="text-[12px] text-muted-foreground">{job.org_name}</p>
          </div>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={onClose} data-testid="cover-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-1.5">Tone</p>
            <div className="flex flex-wrap gap-1.5">
              {TONES.map((t) => (
                <button key={t.v} onClick={() => setTone(t.v)}
                        className={`rounded-full text-[11px] px-2.5 py-1 border ${tone === t.v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                        data-testid={`cover-tone-${t.v}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-1.5">Format</p>
            <div className="flex flex-wrap gap-1.5">
              {VERSIONS.map((v) => (
                <button key={v.v} onClick={() => setVersion(v.v)}
                        className={`rounded-full text-[11px] px-2.5 py-1 border ${version === v.v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                        data-testid={`cover-version-${v.v}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">{VERSIONS.find((vv) => vv.v === version)?.hint}</p>
          </div>
        </div>

        <textarea
          className="tb-input"
          rows={2}
          placeholder="Optional: a custom note to weave in (e.g. 'I led the CBSE Math Olympiad win this year')"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="cover-note"
        />

        {content && (
          <div className="mt-3 rounded-2xl border border-border bg-muted/40 p-3 max-h-72 overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed" data-testid="cover-output">
            {content}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
            <Coins className="h-3 w-3" /> 5 TeeBee Coins per generation
          </p>
          <div className="flex gap-2">
            {content && (
              <Button variant="outline" className="rounded-full" onClick={copy} data-testid="cover-copy">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
            )}
            <Button onClick={generate} disabled={loading} className="rounded-full" data-testid="cover-generate">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              {content ? "Re-generate" : "Generate"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
